<script setup lang="ts">
/**
 * App shell — 仿照 brood-oci 收敛到 @brood/layout-console 的 ConsoleLayout
 * (sidebar 变体);用户登录/账号菜单从旧版手搓侧栏底部移到 **topbar 右上角**
 * (UUserMenu in #header-actions),与 brood-oci / brood-passgate 等 fleet
 * 站点一致。
 *
 * 保留旧 SidebarLayout 的全部机制:
 * - 登录:一击直跳后端代理的飞书 OAuth(authApi.getLoginUrl),无中间 popover;
 * - 登出:RFC-0097 单点登出 — 同源 POST /api/auth/logout 清共享 SSO cookie
 *   + best-effort 跨域撤服务端会话 + 清本地 token + 整页回首页(走墙);
 * - 调整角色:RFC-0009 §5 Pattern B —— UUserMenu 内建切换器 + @brood/component-login
 *   的 useRoleSwitch(应用内不离开页面;无可切角色时切换器自动隐藏);
 * - 快捷键 `?` → ShortcutsDialog;语言切换;passport 个人资料外链;
 * - JIT-admin elevated token 到期提醒;ToastViewport 单例。
 */
import { computed, onMounted, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import {
  Squares2X2Icon,
  CubeIcon,
  ArrowsRightLeftIcon,
  CalendarDaysIcon,
  CommandLineIcon as KeyboardIcon,
  LanguageIcon,
} from "@heroicons/vue/24/outline";
import {
  ConsoleLayout,
  useConsoleNav,
  type ConsoleConfig,
  type NavGroup,
} from "@brood/layout-console";
import { UButton, UMenuItem, useElevatedTokenWarning } from "@brood/ui-kit";
import { UserMenuWithRoleSwitch } from "@brood/component-login";
import { meApi } from "@/api";
import { AUTH_PREFIX } from "@/config";
import { useKeyboardShortcuts } from "@/composables/useKeyboardShortcuts";
import { setLocale, SUPPORTED_LOCALES, type LocaleCode } from "@/plugins/i18n";
import ShortcutsDialog from "@/components/ShortcutsDialog.vue";
import ToastViewport from "@/components/ToastViewport.vue";

const { t, locale } = useI18n({ useScope: "global" });
const nav = useConsoleNav();

const PASSPORT_BASE_URL =
  (import.meta.env.VITE_PASSPORT_URL as string | undefined) || "https://passport.brood.neolix.net";
const passportProfileUrl = `${PASSPORT_BASE_URL.replace(/\/+$/, "")}/profile`;

function switchLocale(code: LocaleCode): void {
  if (locale.value === code) return;
  setLocale(code);
}

// 快捷键 `?` 打开帮助 — dialog ref 直接转发给 composable(纯 DOM 命令式
// 打开,不走 Vue 状态)。
const shortcutsDialog = useTemplateRef<InstanceType<typeof ShortcutsDialog>>("shortcutsDialog");
useKeyboardShortcuts({
  onHelp: () => shortcutsDialog.value?.open(),
});

// 角色提升 — RFC-0009 §5 Pattern B / RFC-0165(应用内不离开页面)。旧 Pattern A 整页跳
// passgate ``/pick-role``(``useRoleRedirect``)已下线,改用共享 ``UserMenuWithRoleSwitch``
// 单组件(``@brood/component-login``):入口 + 弹窗 + token 适配全在组件内,经本应用 BFF
// 代理(``/api/auth/role-options`` + ``/elevate-token``,见 release_channel/api/auth.py)调
// passgate,拿新 access token 写回 localStorage 后刷新 /me 角色态。
//
// 本仓无 pinia auth store,token 历来存 localStorage.access_token(api/index.ts 拦截器
// 注 Bearer),故给组件的 auth 三件套直接读写 localStorage;无 refresh token,setTokens 只取
// access。
const currentRole = computed(() => me.value?.platform_role ?? "");

function _readAccessToken(): string {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem("access_token") || "";
}

function _writeAccessToken(access: string): void {
  if (typeof window !== "undefined" && access) {
    window.localStorage.setItem("access_token", access);
  }
}

// JIT-admin 短效 elevated token 到期提醒(cookie auth,谓词与 brood-passgate
// core/auth/roles.py 的 ELEVATED 集合对齐)。
function _readPassgateCookie(): string {
  if (typeof document === "undefined") return "";
  const m = document.cookie.match(/(?:^|;\s*)passgate_token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}
useElevatedTokenWarning({
  getToken: _readPassgateCookie,
  isElevatedRole: (r: string) => r === "owner" || r === "admin",
  onWarn: (s: number) => {
    // eslint-disable-next-line no-console
    console.warn(`Elevated role expiring in ~${Math.ceil(s / 60)} min`);
  },
  onExpired: () => {
    // eslint-disable-next-line no-console
    console.warn("Elevated role expired; reverted to default.");
  },
});

// Navigation — webuikit 分组(浏览 / 管理)。
const navigation = computed<NavGroup[]>(() => [
  {
    title: t("navGroups.browse"),
    items: [
      { key: "/dashboard", label: t("nav.dashboard"), path: "/dashboard", icon: Squares2X2Icon },
      { key: "/software", label: t("nav.software"), path: "/software", icon: CubeIcon },
      { key: "/channels", label: t("nav.channels"), path: "/channels", icon: ArrowsRightLeftIcon },
    ],
  },
  {
    title: t("navGroups.manage"),
    items: [
      { key: "/trains", label: t("nav.trains"), path: "/trains", icon: CalendarDaysIcon },
    ],
  },
]);

const config = computed<ConsoleConfig>(() => ({
  brand: {
    name: t("app.name"),
    logoText: "RC",
  },
  variant: "sidebar",
  // 6.17.1+: 顶栏 brand 区 + 侧栏页脚自动渲染 AppVersionChip（自取 /api/version），
  // 取代手动 version ref + versionApi.get()。
  version: true,
  navigation: navigation.value,
  showStyleSwitcher: false,
  // 主题工作室入口（@brood/layout-console 6.3.2，右上角 🎨）仅 owner/admin 可见。
  // 本仓用 #header-actions 渲染用户区、不设 config.user，故显式传该字段（fail-closed）。
  themeStudioAdmin: ["owner", "admin"].includes(
    (me.value?.platform_role ?? "").trim().toLowerCase(),
  ),
}));

interface Me {
  sub: string;
  name?: string;
  email?: string;
  roles?: Record<string, string>;
  // 平台级单一角色(/me 的 platform_role),喂给右上角用户菜单的角色徽章,
  // 与 brood-oci 的 auth.user.role 等价 —— 没有它徽章不渲染,trigger 比
  // brood-oci 窄一截(本次对齐的就是这个 size 差异)。
  platform_role?: string;
}

const me = ref<Me | null>(null);
const meLoaded = ref(false);

const userInfo = computed(() =>
  me.value
    ? {
        name: me.value.name || me.value.sub,
        email: me.value.email || me.value.sub,
        avatarUrl: "",
      }
    : null,
);

// /me 拉取(单独抽函数,供 onMounted 首拉 + useRoleSwitch.refreshUser 切角色后刷新)。
// 任何失败都回落到空态,绝不阻塞 shell 渲染 / 切角色流程。
async function fetchMe(): Promise<void> {
  try {
    const r = await meApi.get();
    me.value = r.data;
  } catch {
    me.value = null;
  } finally {
    meLoaded.value = true;
  }
}

onMounted(() => {
  void fetchMe();
  // 可切换角色列表由 RoleSwitchDialog 在打开时按需拉取,无需在挂载时预取。
});

// 登录: 触发登录墙 302 → passport 统一登录页(墙已 broker LIVE, main.py
// login_use_passport_broker=True)。不再走 /api/auth/feishu 代理(A 残留)——
// 墙直跳 passport, 登录后回 /。
async function signIn(): Promise<void> {
  window.location.assign("/");
}

// 登出:RFC-0128 可靠登出。登录墙(BroodLoginWall)对 passgate
// ``passgate_sso_rt_<env>`` / passport ``passport_sso_rt_<env>`` 两个共享 SSO
// cookie **任一有效即放行**。这两个种在父域 ``.brood.neolix.net``、HttpOnly,
// 前端 JS 读不到、跨域 fetch 各自 logout 端点清除也不可靠 → 旧版"只清非-HttpOnly
// 的 ``passgate_token`` + 跨域 POST passgate sso/logout 后整页跳 ``/``"漏清
// passport_sso_rt + 墙的 SSO 校验仍通过 → 被静默登回。
//
// 修法:清本地非-HttpOnly token 后,末步**整页**导航到同源后端 ``/logout``。
// 后端(broodbridge.install_logout_route)用同源 Set-Cookie(Max-Age=0、
// Domain=父域)100% 可靠删三类 cookie(本应用 ``passgate_token`` +
// ``passgate_sso_rt_*`` + ``passport_sso_rt_*``)→ 302 到墙豁免的 ``/login``
// 停留页。彻底登出、不再静默重登;绝不跳 ``/``。
function signOut(): void {
  // 清本地非-HttpOnly token / localStorage(后端 /logout 删 HttpOnly cookie)。
  if (typeof document !== "undefined") {
    const past = "Thu, 01 Jan 1970 00:00:00 GMT";
    document.cookie = `passgate_token=; path=/; expires=${past}; SameSite=Lax`;
  }
  if (typeof window !== "undefined") {
    window.localStorage.removeItem("access_token");
    // 末步:整页导航到同源 /logout → 后端删三类 cookie → 302 /login 停留页。
    window.location.assign("/logout");
  }
}

// useConsoleNav 内部读 route 做侧栏高亮;引用以避免 unused-var lint。
void nav;
</script>

<template>
  <ConsoleLayout :config="config">
    <template #header-actions>
      <!-- 登录态:右上角用户菜单(brood-oci 同款)。 -->
      <UserMenuWithRoleSwitch
        v-if="userInfo"
        :user="userInfo"
        :current-role="currentRole"
        :auth="{ getToken: _readAccessToken, setTokens: _writeAccessToken, refreshUser: fetchMe }"
        :endpoints="{ roleOptions: `${AUTH_PREFIX}/role-options`, elevate: `${AUTH_PREFIX}/elevate-token` }"
        :labels="{ logout: t('auth.signOut'), switchRole: t('auth.adjustRole') }"
        :dialog-labels="{ title: t('auth.adjustRole') }"
        @logout="signOut"
      >
        <template #extra-items>
          <!-- passport 个人资料(时区/语言等服务端设置)。 -->
          <UMenuItem>
            <a
              :href="passportProfileUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="flex w-full items-center justify-between px-3 py-2 text-sm"
            >
              <span>{{ t("user.profile") }}</span>
              <span aria-hidden="true" class="text-[var(--u-text-muted)]">↗</span>
            </a>
          </UMenuItem>
          <!-- 调整角色已收敛为 UserMenuWithRoleSwitch 内的「切换角色…」入口 → 弹窗
               (Pattern B),不再走整页 /pick-role 跳转。 -->
          <!-- 快捷键帮助(? 同效)。 -->
          <UMenuItem>
            <button
              type="button"
              class="flex w-full items-center justify-between border-t border-[var(--u-border)] px-3 py-2 text-sm"
              @click="shortcutsDialog?.open()"
            >
              <span class="flex items-center gap-1.5">
                <KeyboardIcon class="h-4 w-4" aria-hidden="true" />
                {{ t("sidebar.shortcuts") }}
              </span>
              <kbd
                class="rounded border border-[var(--u-border)] px-1 py-0.5 font-mono text-[10px] leading-none text-[var(--u-text-muted)]"
                aria-hidden="true"
                >?</kbd
              >
            </button>
          </UMenuItem>
          <!-- 语言切换。 -->
          <div class="border-t border-[var(--u-border)] pt-1">
            <div
              class="flex items-center gap-1.5 px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--u-text-muted)]"
            >
              <LanguageIcon class="h-3 w-3" aria-hidden="true" />
              {{ t("lang.title") }}
            </div>
            <UMenuItem v-for="code in SUPPORTED_LOCALES" :key="code" @click="switchLocale(code)">
              <span class="flex w-full items-center justify-between py-1 pl-4 pr-3 text-sm">
                <span>{{ t(`lang.${code}`) }}</span>
                <span v-if="locale === code" class="text-[var(--u-primary)]" aria-hidden="true"
                  >✓</span
                >
              </span>
            </UMenuItem>
          </div>
        </template>
      </UserMenuWithRoleSwitch>
      <!-- 登出态:右上角一击登录(直跳 OAuth)。meLoaded 前不渲染,防
           「登录」闪现后变用户名的 FOUC。 -->
      <UButton v-else-if="meLoaded" variant="text" size="sm" @click="signIn">
        {{ t("auth.signIn") }}
      </UButton>
    </template>

    <slot />

    <!-- 快捷键帮助 — 常驻挂载(命令式 open/close),`?` 与菜单入口共用。 -->
    <ShortcutsDialog ref="shortcutsDialog" />
    <!-- 单例 toast viewport。 -->
    <ToastViewport />
  </ConsoleLayout>
</template>
